import Feature from "ol/Feature";
import GeoJSON from "ol/format/GeoJSON";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style } from "ol/style";
import { selectService } from "@/services/map/selectService";
import { IMPORT_PROVIDER, importedLayerRegistry } from "@/services/layers/importedLayerRegistry";
import { attributesTableService } from "@/services/layers/attributesTable";

// Estilo del resaltado de entidades objetivo seleccionadas por clic — azul
// sólido, deliberadamente distinto del naranja que ya usa advancedRoiService.js
// para la región de interés, para no confundir ambos overlays en el mapa.
const selectionStyle = new Style({
  stroke: new Stroke({ color: "#2563eb", width: 3 }),
  fill: new Fill({ color: "rgba(37, 99, 235, 0.35)" }),
});

const geojsonFormat = new GeoJSON();

// La geometría que devuelven fetchWMSFeatureInfo/fetchArcGISFeatureInfoWithGeometry
// ya viene en EPSG:3857 (mismo SRS pedido en la consulta) — sin dataProjection
// explícito, GeoJSON asumiría 4326 por defecto y reproyectaría mal.
const readGeometry3857 = geometry => geojsonFormat.readGeometry(geometry, { dataProjection: "EPSG:3857", featureProjection: "EPSG:3857" });

class TargetFeatureSelectionService {
  constructor() {
    this.map = null;
    this.source = new VectorSource({ wrapX: false });
    this.layer = new VectorLayer({ source: this.source, style: selectionStyle, zIndex: 10001 });
    this.clickListener = null;
    this.targetLayer = null;
    this.selection = new Map(); // String(id) -> { id, geometry }
    this.onChange = null;
    // Ver stop() — guarda qué capas tenía selectService antes de vaciarlas.
    this.suppressedSelectLayers = null;
  }

  init(map) {
    if (this.map === map) return;
    this.detach();
    this.map = map;
    this.ensureLayer();
  }

  ensureLayer() {
    if (!this.map) return;
    if (!this.map.getLayers().getArray().includes(this.layer)) this.map.addLayer(this.layer);
  }

  isActive() {
    return !!this.clickListener;
  }

  // start: (re)activa el modo de selección por clic para targetLayer. Si ya
  // había una selección previa (filtro restaurado/guardado), initialFeatures
  // trae sus geometrías ya resueltas (ver AdvancedFilterPanel.vue) para poder
  // resaltarlas de entrada sin esperar un clic nuevo.
  start(targetLayer, initialFeatures, onChange) {
    if (!this.map) return;
    this.detachListener();
    this.targetLayer = targetLayer;
    this.onChange = onChange;
    this.selection = new Map((initialFeatures || []).map(({ id, geometry }) => [String(id), { id, geometry }]));
    this.render();
    // Se probó primero coordinar con la bandera compartida
    // map.set("suppressFeaturePopups", …) que ya usa advancedRoiService.js
    // para su propio dibujo de ROI, pero comparten la MISMA propiedad del
    // mapa — algo en ese ciclo de vida (probablemente stopInteractions() de
    // advancedRoiService, que también la apaga) la reseteaba a `false` entre
    // un clic y el siguiente, dejando el popup normal de "Consulta" volver a
    // aparecer a partir del segundo clic. En vez de perseguir esa carrera,
    // se vacía directamente la lista de capas que selectService.js puede
    // identificar mientras este modo está activo — su propio
    // getPointFeatures() corta sync apenas ve una lista vacía (sin red, sin
    // ventana de carrera posible), así que no tiene nada que mostrar sea
    // cual sea el estado de esa bandera compartida.
    this.suppressedSelectLayers = selectService.activeLayers;
    selectService.setActiveLayers([]);
    this.map.getViewport().style.cursor = "crosshair";
    this.clickListener = event => this.handleClick(event);
    this.map.on("singleclick", this.clickListener);
  }

  detachListener() {
    if (this.clickListener && this.map) {
      this.map.un("singleclick", this.clickListener);
    }
    this.clickListener = null;
    if (this.suppressedSelectLayers !== null) {
      selectService.setActiveLayers(this.suppressedSelectLayers);
      this.suppressedSelectLayers = null;
    }
  }

  async handleClick(event) {
    if (!this.targetLayer) return;
    const layer = this.targetLayer;
    const provider = layer.provider ? layer.provider.name : null;
    let picked = null;
    try {
      if (provider === "geoserver") {
        const view = this.map.getView();
        const result = await selectService.fetchWMSFeatureInfo(layer, event.coordinate, view.getResolution(), view.getProjection());
        const feature = result && result.features && result.features[0];
        if (feature && feature.id != null) picked = { id: feature.id, geometry: feature.geometry };
      } else if (provider === "arcgis") {
        const view = this.map.getView();
        const result = await selectService.fetchArcGISFeatureInfoWithGeometry(layer, event.coordinate, view.getResolution());
        const feature = result && result.features && result.features[0];
        if (feature && feature.id != null) picked = { id: feature.id, geometry: feature.geometry };
      } else if (provider === IMPORT_PROVIDER) {
        picked = this.pickImportedFeature(layer, event.pixel);
      }
    } catch (error) {
      console.warn("[targetFeatureSelectionService] No se pudo identificar la entidad clickeada:", error);
      return;
    }
    if (!picked || !picked.geometry) return;
    this.toggle(picked);
  }

  pickImportedFeature(layer, pixel) {
    const entry = importedLayerRegistry.get(layer.id);
    if (!entry) return null;
    let matchedFeature = null;
    this.map.forEachFeatureAtPixel(pixel, (feature, olLayer) => {
      if (matchedFeature || !olLayer || olLayer.get("id") !== layer.id) return;
      matchedFeature = feature;
    }, { hitTolerance: 16, layerFilter: olLayer => olLayer.get("id") === layer.id });
    if (!matchedFeature) return null;
    const index = entry.records.findIndex(record => record.feature === matchedFeature);
    if (index === -1 || !entry.records[index].row.geometry) return null;
    return { id: index, geometry: entry.records[index].row.geometry };
  }

  toggle(picked) {
    const key = String(picked.id);
    if (this.selection.has(key)) this.selection.delete(key);
    else this.selection.set(key, picked);
    this.render();
    this.emitChange();
  }

  removeId(id) {
    this.selection.delete(String(id));
    this.render();
    this.emitChange();
  }

  emitChange() {
    if (this.onChange) this.onChange(Array.from(this.selection.values()).map(item => item.id));
  }

  render() {
    this.ensureLayer();
    this.source.clear();
    this.selection.forEach(({ geometry }) => {
      try {
        const olGeometry = readGeometry3857(geometry);
        if (olGeometry) this.source.addFeature(new Feature(olGeometry));
      } catch (error) {
        // Geometría inválida/no soportada — se ignora el resaltado de esa
        // entidad puntual sin romper el resto de la selección.
      }
    });
  }

  // stop: pausa — apaga el listener de clic y OCULTA el resaltado del mapa
  // (el usuario pidió explícitamente que salir del modo "clic" saque el
  // resaltado de la vista), pero conserva `selection`/targetLayer/onChange
  // en memoria. La selección real vive en el draft (targetSelection.ids,
  // ver AdvancedFilterPanel.vue), así que no se pierde: start() la vuelve a
  // pedir y resaltar la próxima vez que se reactive este modo, sin
  // necesitar un clic nuevo. Sólo cambiar de capa objetivo, desactivar la
  // sección "Filtro por capa", cerrar la tabla, o recargar la borran de
  // verdad (ver clear()/detach() y HIDE_TABLE en store/modules/attributeTable.js).
  stop() {
    this.detachListener();
    this.source.clear();
    if (this.map) this.map.getViewport().style.cursor = "";
  }

  // clear: reset completo — se llama al cambiar de capa objetivo, desactivar
  // la sección de filtro por capa, o cerrar la tabla de atributos.
  clear() {
    this.stop();
    this.targetLayer = null;
    this.onChange = null;
    this.selection = new Map();
    this.source.clear();
  }

  detach() {
    this.clear();
    if (this.map) this.map.removeLayer(this.layer);
    this.map = null;
  }
}

export const targetFeatureSelectionService = new TargetFeatureSelectionService();

// resolveInitialFeatures: dado un targetLayer y una lista de ids ya guardados
// (filtro restaurado desde sessionStorage o Filtros guardados), resuelve sus
// geometrías para poder resaltarlos apenas se entra en modo selección — sin
// esto, reabrir un filtro guardado con selección por clic mostraría los chips
// pero sin resaltado hasta el próximo clic.
export const resolveInitialSelectionFeatures = async (targetLayer, metadata, ids) => {
  if (!ids || !ids.length) return [];
  return attributesTableService.fetchTargetFeaturesByIds(targetLayer, metadata, ids, null);
};
